import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { fabric } from 'fabric';
import {
  debounceTime,
  Observable,
  Subject,
  Subscription,
  take,
  throttleTime,
} from 'rxjs';

import {
  FloorPlan,
  FloorPlanSection,
  FloorPlanTable,
  GroupedItems,
} from '@shared/floor-plan/models';
import { FrontendScrollbarComponent } from '@tablesready/frontend-scrollbar';
import { Resource } from '@shared/resource/models';
import { TableSizes } from '@shared/floor-plans';
import { Shapes } from '@shared/core';
import {
  CANVAS_DARK_THEME,
  CANVAS_GRID_CELL_SIZE,
  CANVAS_LIGHT_THEME,
  CANVAS_MIN_SHAPE_SIZE,
  CanvasColors,
  CanvasObjectMultiSelected,
  CanvasObjectSelected,
  CanvasTableChildren,
  CLONED_TABLE_ID,
  FloorPlanDraggable,
  FloorPlanDragState,
  FloorPlanForSwitcher,
  SHAPES_DIMENSIONS,
} from './canvas.constants';
import { AppTheme, ThemeSwitchService } from '@tablesready/frontend-theme-switch';

@Component({
  selector: 'frontend-canvas',
  templateUrl: './canvas.component.html',
  styleUrls: ['./canvas.component.scss'],
})
export class CanvasComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @ViewChild('canvasFloatRef') canvasFloatRef?: ElementRef;
  @ViewChild('controlsRef') controlsRef?: ElementRef;
  @ViewChild('canvasAvailableArea') canvasAvailableAreaRef?: ElementRef;
  @ViewChild(FrontendScrollbarComponent) scrollbarRef?: FrontendScrollbarComponent;

  @Input() canvasId = 'fabricSurface';
  @Input() visible = true;
  @Input() showGrid = true;
  @Input() gridCellSize = CANVAS_GRID_CELL_SIZE;
  @Input() targetCanvasWidth = 2000;
  @Input() targetCanvasHeight = 2000;
  @Input() leftLayoutOffsetPx = 100;
  @Input() canvasAreaWidth = '100%';
  @Input() canvasAreaHeightPx = 100;
  @Input() gridSnapping = true;
  @Input() borderWidth = 1;
  @Input() sectionBorderWidth = 4;
  @Input() showZoomControls = true;
  @Input() showFloorsEdit = false;
  @Input() transformEnabled = true;
  @Input() multiTransformSelectMode = false;
  @Input() customMultiSelectEnabled = false;
  @Input() allowMultiSelectByUser = true;
  @Input() allowDeselectionByUser = true;
  @Input() showAutoZoomRegion = false;
  @Input() canvasAreaOffset = 30;
  @Input() canvasTopOffset = 30;
  @Input() customSelectMode = false;
  @Input() allowCustomSelection = false;
  @Input() allowOnlySectionIdSelection?: string;
  @Input() floorPlanItems: FloorPlanForSwitcher[] = [];
  @Input() selectedObjectId?: string;
  @Input() resourcesMap?: Map<string, Resource>;
  @Input() showDisabledTables = true;
  @Input() allowObjectCustomSelection = false;
  @Input() isOnFreePlan = true;
  @Input() getItems?: (floorPlanId: string) => Observable<GroupedItems>;

  @Output() multiTransformSelectModeChange = new EventEmitter<boolean>();
  @Output() dropNewObject = new EventEmitter<[number, number]>();
  @Output() floorChanged = new EventEmitter<string>();
  @Output() planEdit = new EventEmitter<FloorPlan>();
  @Output() objectMultiSelected = new EventEmitter<CanvasObjectMultiSelected>();
  @Output() objectSelected = new EventEmitter<CanvasObjectSelected>();
  @Output() clickedOnCanvas = new EventEmitter<void>();
  @Output() updatePartyWithResource = new EventEmitter<[string, string]>();
  @Output() htmlDragStart = new EventEmitter<boolean>();
  @Output() objectDragStart = new EventEmitter<boolean>();
  @Output() objectDragEnd = new EventEmitter<CanvasObjectSelected>();

  isLoading = true;
  minZoom = 0.25;
  maxZoom = 1.5;

  zoomSteps = [this.minZoom, 0.375, 0.5, 0.625, 0.75, 1, 1.25, this.maxZoom];
  defaultZoomStepIndex = 2;
  currentZoomStepIndex = this.defaultZoomStepIndex;
  scrollableAreaWidth = this.targetCanvasWidth;
  scrollableAreaHeight = this.targetCanvasHeight;
  actualCanvasWidth = this.targetCanvasWidth;
  actualCanvasHeight = this.targetCanvasHeight;
  afterRender$ = new Subject<boolean>();

  zoomControls = [
    {
      name: '+',
      value: 1,
    },
    {
      name: '-',
      value: -1,
    },
  ];

  floorPlanId?: string;
  dragState: FloorPlanDragState = {
    dragStarted: false,
    moveX: -1,
    moveY: -1,
    startX: -1,
    startY: -1,
    offsetX: -1,
    offsetY: -1,
  };

  protected canvas?: fabric.Canvas;
  private objectPattern?: fabric.Pattern;
  private sectionByTableId: Record<string, FloorPlanSection> = {};
  private currentTheme?: AppTheme;
  private themeColors?: CanvasColors;
  private multiselectColor = 'blue';
  private activeSelection?: fabric.ActiveSelection;
  private multiTransformSelectedObjects: fabric.Object[] = [];
  private readonly rerenderCanvas$ = new Subject<string>();
  private readonly multiSelectedObjectIds = new Set<string>();
  private readonly multiselectColorSource$ = new Subject<string>();
  private readonly subscriptions = new Subscription();
  private readonly allControls = ['mt', 'mb', 'ml', 'mr', 'mtr', 'br', 'bl', 'tr', 'tl'];
  private readonly circleControls = ['mt', 'mb', 'ml', 'mr'];

  constructor(
    private readonly themeSwitch: ThemeSwitchService,
    private readonly cdr: ChangeDetectorRef,
  ) { }

  setMultiselectColor(color: string): void {
    this.multiselectColorSource$.next(color);
  }

  clearCanvas(): void {
    const objects = this.canvas?.getObjects('group');
    objects?.forEach((box) => {
      this.canvas?.remove(box);
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.init();
    });
  }

  autoZoom(tables: FloorPlanTable[]): void {
    this.afterRender$
      .pipe(take(1))
      .subscribe(() => {
        this.autoZoomInternal(tables);
      });
  }

  redrawScrollbars(caller: string): void {
    this.cdr.detectChanges();

    if (this.canvas) {
      this.setScrollSize(this.canvas.getZoom());
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['transformEnabled'] !== undefined) {
      const objects = this.canvas?.getObjects('group');
      if (!objects) {
        return;
      }

      if (this.transformEnabled) {
        this.removeCustomSelectionBoxes();
      }

      this.canvas?.discardActiveObject();
      this.customSelectMode = !this.transformEnabled;
      this.setTransformControlsState(objects, this.allControls, this.transformEnabled, !this.transformEnabled);
      this.canvas?.renderAll();
    }
  }

  ngOnInit(): void {
    const currentTheme = this.themeSwitch.getSavedTheme();
    this.currentTheme = currentTheme;
    this.themeColors = this.themeToColors(currentTheme);
    this.initObjectPattern();

    const DEBOUNCE = 250;
    const colorSub = this.multiselectColorSource$
      .pipe(throttleTime(DEBOUNCE))
      .subscribe((color) => {
        this.multiselectColor = color;
      });
    this.subscriptions.add(colorSub);

    const themeSub = this.themeSwitch.themeChange$
      .subscribe((theme) => {
        if (theme !== this.currentTheme) {
          this.currentTheme = theme;
          this.changeTheme(this.themeToColors(theme));
          this.initObjectPattern();

          if (this.floorPlanId !== undefined) {
            this.onFloorChange(this.floorPlanId, 'builder theme change');
          }
        }
      });
    this.subscriptions.add(themeSub);

    const RESIZE_EVENT_DEBOUNCE = 200;
    const canvasRerenderSub = this.rerenderCanvas$
      .pipe(debounceTime(RESIZE_EVENT_DEBOUNCE))
      .subscribe((reason) => {
        this.redrawScrollbars('this.rerenderCanvas$');
        this.setCanvasDimensions();
      });
    this.subscriptions.add(canvasRerenderSub);

    window.addEventListener('resize', this.windowResizeBound);
    document.addEventListener('mousedown', this.dragStart);
    document.addEventListener('touchstart', this.dragStart);
    document.addEventListener('mousemove', this.drag);
    document.addEventListener('touchmove', this.drag);
    document.addEventListener('mouseup', this.dragEnd);
    document.addEventListener('touchend', this.dragEnd);
  }

  changeTheme(newTheme: CanvasColors): void {
    if (!this.canvas) {
      return;
    }

    this.themeColors = newTheme;
    this.canvas.backgroundColor = newTheme.backgroundColor;
    this.canvas?.getObjects('line').forEach((line) => {
      this.canvas?.remove(line);
    });

    this.drawGrid();
    this.canvas?.getObjects('group').forEach((group) => {
      const id = group.data?.id;
      if (!id) {
        return;
      }

      this.setTableRotation(id, group.angle);
    });

    this.canvas.renderAll();
  }

  onFloorChange(floorPlanId: string, reason: string, callback?: () => void): void {
    if (!this.getItems) {
      return;
    }

    this.deselectAllObjects();

    this.getItems(floorPlanId)
      .pipe(take(1))
      .subscribe((items) => {
        this.clearCanvas();
        this.floorChanged.emit(floorPlanId);
        this.sectionByTableId = {};

        items.sections.forEach((section) => {
          section.tableIds.forEach((tableId) => {
            this.sectionByTableId[tableId] = section;
          });
        });

        items.tables.forEach((item) => {
          if (!item.resourceId || !this.resourcesMap) {
            return;
          }

          const res = this.resourcesMap.get(item.resourceId);
          if (!res) {
            return;
          }

          const sizeLabel = `${res.capacity.min}-${res.capacity.max}`;
          this.addTable(
            item.id,
            item.name,
            sizeLabel,
            '',
            item.width,
            item.height,
            item.top,
            item.left,
            item.shape,
            !res.enabled,
            false,
            0,
            item.rotation,
            item.resourceId,
            this.sectionByTableId[item.id]?.id,
            false,
          );
        });

        items.objects.forEach((item) => {
          this.addObject(
            item.id,
            item.name,
            item.width,
            item.height,
            item.top,
            item.left,
            item.shape,
            false,
            item.rotation,
          );
        });

        if (callback !== undefined) {
          callback();
        }

        this.isLoading = false;
      });
  }

  addObject(
    id: string,
    name = '',
    width = CANVAS_MIN_SHAPE_SIZE,
    height = CANVAS_MIN_SHAPE_SIZE,
    top = 0,
    left = 0,
    shape = Shapes.square,
    makeActive?: boolean,
    rotation?: number,
  ): fabric.Group | null {
    if (!this.canvas) {
      return null;
    }

    const children: fabric.Object[] = [];
    if (shape !== Shapes.circle) {
      const rect = new fabric.Rect({
        data: { type: 'shape' },
        fill: this.objectPattern,
        strokeWidth: this.borderWidth,
        stroke: this.themeColors?.shapeStroke,
        hoverCursor: 'pointer',
        originX: 'center',
        originY: 'center',
        selectable: false,
        evented: false,
        width: width - this.borderWidth,
        height: height - this.borderWidth,
        objectCaching: false,
      });
      this.setTransformControlsState([rect], this.allControls, false);
      children.push(rect);
    }

    if (shape === Shapes.circle) {
      const circle = new fabric.Circle({
        data: { type: 'shape' },
        radius: (width - this.borderWidth) / 2,
        fill: this.objectPattern,
        strokeWidth: 1,
        stroke: this.themeColors?.shapeStroke,
        hoverCursor: 'pointer',
        originX: 'center',
        originY: 'center',
        selectable: false,
        evented: false,
        objectCaching: false,
      });
      this.setTransformControlsState([circle], this.circleControls, true);
      children.push(circle);
    }

    const objectName = new fabric.Text(name, {
      data: {
        type: 'name',
        fullText: name,
      },
      fontFamily: 'Source Sans Pro',
      width,
      height,
      fontWeight: 400,
      fontStyle: 'normal',
      fontSize: 10,
      fill: this.themeColors?.textColor,
      originX: 'center',
      textAlign: 'center',
      originY: 'center',
      selectable: false,
      evented: false,
      objectCaching: false,
    });
    this.setTransformControlsState([objectName], this.allControls, false);
    objectName.set('text', this.getTextForShapeWidth(name, width));
    children.push(objectName);

    const group = new fabric.Group(children, {
      data: {
        id,
        shape,
        objectType: 'object',
      },
      left: Math.round(left),
      top: Math.round(top),
      lockScalingFlip: true,
      centeredRotation: false,
      centeredScaling: false,
      objectCaching: false,
      selectable: !this.isOnFreePlan,
      evented: !this.isOnFreePlan,
      opacity: this.isOnFreePlan ? 0.5 : 1,
    });

    if (rotation !== undefined) {
      group.rotate(this.normalizeRotation(rotation));
    }

    if (shape === Shapes.circle) {
      this.setTransformControlsState([group], this.circleControls, false);
    }

    this.canvas.add(group);
    this.checkGroupCoords(group);

    if (makeActive) {
      this.selectTableById(id);
    }

    return group;
  }

  addTable(
    id: string,
    name = '',
    size = '',
    time = '',
    width = CANVAS_MIN_SHAPE_SIZE,
    height = CANVAS_MIN_SHAPE_SIZE,
    top = 0,
    left = 0,
    shape = Shapes.square,
    disabled = false,
    isServing = false,
    partyCompletedPercentage = 0,
    rotation?: number,
    resourceId?: string,
    sectionId = '',
    makeActive?: boolean,
  ): fabric.Group | null {
    if (!this.canvas || (disabled && !this.showDisabledTables)) {
      return null;
    }

    const noSection = sectionId !== undefined && sectionId !== '';
    const children: fabric.Object[] = [];
    let sectionWidth = 0;

    if (noSection) {
      sectionWidth = this.sectionBorderWidth;
    }

    if (shape !== Shapes.circle) {
      const rect = new fabric.Rect({
        data: { type: 'shape' },
        fill: this.themeColors?.shapeFill,
        strokeWidth: this.borderWidth,
        stroke: this.themeColors?.shapeStroke,
        hoverCursor: 'pointer',
        originX: 'center',
        originY: 'center',
        selectable: false,
        evented: false,
        width: width - this.borderWidth - sectionWidth,
        height: height - this.borderWidth - sectionWidth,
        objectCaching: false,
      });
      this.setTransformControlsState([rect], this.allControls, false);
      children.push(rect);

      const sectionBorder = new fabric.Rect({
        data: { type: 'section' },
        strokeWidth: sectionWidth,
        fill: 'rgba(0,0,0,0)',
        stroke: this.sectionByTableId[String(id)]?.color,
        hoverCursor: 'pointer',
        originX: 'center',
        originY: 'center',
        selectable: false,
        evented: false,
        width: width - sectionWidth,
        height: height - sectionWidth,
        objectCaching: false,
      });
      this.setTransformControlsState([sectionBorder], this.allControls, false);
      if (noSection) {
        children.push(sectionBorder);
      }
    }

    if (shape === Shapes.circle) {
      const sectionColor = this.sectionByTableId[String(id)]?.color;
      const circle = new fabric.Circle({
        data: { type: 'shape' },
        radius: (width - this.borderWidth - sectionWidth) / 2,
        fill: this.themeColors?.shapeFill,
        stroke: sectionColor ? 'rgba(0,0,0,0)' : this.themeColors?.shapeStroke,
        strokeWidth: this.borderWidth,
        hoverCursor: 'pointer',
        originX: 'center',
        originY: 'center',
        selectable: false,
        evented: false,
        objectCaching: false,
      });
      this.setTransformControlsState([circle], this.circleControls, true);
      children.push(circle);

      const sectionBorder = new fabric.Circle({
        data: { type: 'section' },
        stroke: sectionColor,
        fill: 'rgba(0,0,0,0)',
        strokeWidth: sectionWidth,
        hoverCursor: 'pointer',
        originX: 'center',
        originY: 'center',
        selectable: false,
        evented: false,
        radius: (width - sectionWidth) / 2,
        objectCaching: false,
      });
      this.setTransformControlsState([sectionBorder], this.circleControls, false);
      if (noSection) {
        children.push(sectionBorder);
      }
    }

    const textOptions: fabric.TextOptions = {
      fontFamily: 'Source Sans Pro',
      width,
      height,
      fontWeight: 400,
      fontStyle: 'normal',
      fontSize: 10,
      fill: this.themeColors?.textColor,
      originX: 'center',
      textAlign: 'center',
      originY: 'center',
      selectable: false,
      evented: false,
    };

    let textVerticalOffset = 0;
    let textHeight = 16;
    if (time === '') {
      textVerticalOffset += 8;
    }

    const resourceName = new fabric.Text(name, {
      ...textOptions,
      fontWeight: 800,
      data: {
        type: 'name',
        fullText: name,
      },
      top: -textHeight + textVerticalOffset,
      objectCaching: false,
    });

    const tableSize = new fabric.Text(disabled ? '(disabled)' : size, {
      ...textOptions,
      data: { type: 'size' },
      top: textVerticalOffset,
      objectCaching: false,
    });

    const partyTime = new fabric.Text(time, {
      ...textOptions,
      data: { type: 'time' },
      top: textHeight,
      height: time === '' ? 0 : textHeight,
      objectCaching: false,
    });

    if (isServing) {
      const progressBarBackground = new fabric.Rect({
        data: { type: 'progressBarBackground' },
        fill: this.themeColors?.shapeStroke,
        originX: 'left',
        originY: 'left',
        selectable: false,
        evented: false,
        height: 3,
        top: resourceName.top,
        width: partyTime.width,
        objectCaching: false,
      });

      const progressBarBodyWidth = progressBarBackground.width * partyCompletedPercentage / 100;
      const progressBarBody = new fabric.Rect({
        data: { type: 'progressBarBody' },
        fill: partyCompletedPercentage === 100 ? '#ee4266' : '#3a74cb',
        originX: 'left',
        originY: 'left',
        selectable: false,
        evented: false,
        height: progressBarBackground.height,
        width: progressBarBodyWidth,
        objectCaching: false,
      });

      const progressBarGroup = new fabric.Group([progressBarBackground, progressBarBody], {
        data: { type: 'progressBar' },
        originX: 'left',
        originY: 'top',
        selectable: !disabled,
        evented: !disabled,
        top: 6,
        left: -progressBarBackground.width / 2,
        width: progressBarBackground.width,
        height: progressBarBackground.height,
        objectCaching: false,
      });

      children.push(progressBarGroup);
    }

    this.setTransformControlsState([resourceName, tableSize, partyTime], this.allControls, false);
    resourceName.set('text', this.getTextForShapeWidth(name, width));
    children.push(partyTime);
    children.push(resourceName);
    children.push(tableSize);

    let shadow = undefined;
    let selectable = !disabled;
    let evented = !disabled;
    let opacity = disabled ? 0.5 : 1;
    if (partyCompletedPercentage === 100) {
      shadow = new fabric.Shadow({
        color: '#ee4266',
        blur: 10,
      });
    }

    if (this.isOnFreePlan) {
      selectable = false;
      evented = false;
      opacity = 0.5;
    }

    const group = new fabric.Group(children, {
      data: {
        id,
        resourceId,
        sectionId,
        objectType: 'table',
        disabled,
      },
      left: Math.round(left),
      top: Math.round(top),
      lockScalingFlip: true,
      centeredRotation: false,
      centeredScaling: false,
      opacity,
      selectable,
      evented,
      objectCaching: false,
      shadow,
    });

    if (rotation !== undefined) {
      group.rotate(this.normalizeRotation(rotation));
    }

    if (shape === Shapes.circle) {
      this.setTransformControlsState([group], this.circleControls, false);
    }

    this.canvas.add(group);
    this.checkGroupCoords(group);

    if (makeActive) {
      this.selectTableById(id);
    }

    return group;
  }

  setResourceId(objectId: string, resourceId: string): void {
    const { group } = this.getObjectsById(objectId);
    if (!group) {
      return;
    }

    group.data = {
      ...group.data,
      resourceId,
    };
  }

  setObjectText(id: string, name: string): void {
    const { group, shape } = this.getObjectsById(id);

    if (!group) {
      return;
    }

    const newGroup = this.addObject(
      id,
      name,
      group.width,
      group.height,
      group.top,
      group.left,
      shape,
      true,
      group.angle,
    );

    if (newGroup) {
      this.scaleGroup(newGroup);
    }

    this.removeGroup(group);
  }

  setTableTextPropName(
    propName: string,
    propValue: string,
    makeActive = true,
    isServing = false,
    partyCompletedPercentage = 0,
    newName?: string,
    newSize?: string,
    newTime?: string,
  ): void {
    const group = this.canvas?.getObjects('group')
      .find(item => item?.data[propName] === propValue) as fabric.Group;

    if (!group) {
      return;
    }

    const {
      nameText,
      capacityText,
      timeText,
      shape,
    } = this.getObjects(group);

    if (!nameText || !capacityText || !timeText) {
      return;
    }

    const resourceId = group.data?.resourceId;
    const sectionId = group.data?.sectionId;

    const newGroup = this.addTable(
      group.data.id,
      newName !== undefined ? newName : nameText?.text,
      newSize !== undefined ? newSize : capacityText?.text,
      newTime !== undefined ? newTime : timeText?.text,
      group.width,
      group.height,
      group.top,
      group.left,
      shape,
      false,
      isServing,
      partyCompletedPercentage,
      group.angle,
      resourceId,
      sectionId,
      makeActive,
    );

    if (newGroup && makeActive) {
      this.canvas?.setActiveObject(newGroup);
      this.scaleGroup(newGroup);
    }

    this.removeGroup(group);
  }

  removeGroup(group: fabric.Group): void {
    let isActiveObject = false;
    this.canvas?.getActiveObjects().forEach((activeItem) => {
      if (activeItem === group) {
        isActiveObject = true;
      }
    });

    if (isActiveObject) {
      return;
    }

    this.canvas?.remove(group);
    this.canvas?.renderAll();
  }

  setTableRotation(id: string, rotation: number): void {
    const group = this.canvas?.getObjects('group').find(item => item.data?.id === id) as fabric.Group;
    if (!group) {
      return;
    }

    group.set('angle', rotation);
    this.canvas?.renderAll();
  }

  setObjectShape(id: string, newShape: Shapes, width?: number, height?: number): void {
    const {
      group,
      nameText,
      capacityText,
      timeText,
    } = this.getObjectsById(id);

    if (!nameText || !capacityText || !group) {
      return;
    }

    const tableWidth = group.width * group.scaleX;
    const tableHeight = group.height * group.scaleY;
    const resourceId = group.data?.resourceId;
    const sectionId = group.data?.sectionId;

    const newGroup = this.addTable(
      id,
      nameText?.text,
      capacityText?.text,
      timeText?.text,
      width ? width : tableWidth,
      height ? height : tableHeight,
      group.top,
      group.left,
      newShape,
      false,
      false,
      0,
      group.angle,
      resourceId,
      sectionId,
      false,
    );

    if (newGroup) {
      this.canvas?.setActiveObject(newGroup);
    }

    this.removeGroup(group);
    this.selectTableById(id);
  }

  setZoomByAmount(amount: number): void {
    this.setZoom(amount);
  }

  setZoomByStep(stepAmount: number): void {
    if (!this.canvas) {
      return;
    }

    if (stepAmount > 0) {
      this.currentZoomStepIndex = Math.min(this.currentZoomStepIndex + stepAmount, this.zoomSteps.length - 1);
    }

    if (stepAmount < 0) {
      this.currentZoomStepIndex = Math.max(this.currentZoomStepIndex + stepAmount, 0);
    }

    this.setZoom(this.zoomSteps[this.currentZoomStepIndex]);
  }

  getZoom(): number {
    if (!this.canvas) {
      return 1;
    }

    return this.canvas?.getZoom();
  }

  resetTableColors(resourcesMap: Map<string, Resource>): void {
    const tables = this.canvas?.getObjects('group')
      .filter(group => group?.data.objectType === 'table') as fabric.Group[];

    if (!tables || !this.currentTheme) {
      return;
    }

    const { shapeFill } = this.themeToColors(this.currentTheme);

    tables.forEach((table) => {
      const resource = resourcesMap.get(table?.data?.resourceId);
      this.setTableTextPropName(
        'id',
        table.data.id,
        false,
        false,
        0,
        resource ? resource.name : undefined,
        resource ? `${resource.capacity.min}-${resource.capacity.max}` : undefined,
        '',
      );
      this.changeTableColor(table, shapeFill);
    });
  }

  setTableColor(resourceId: string, color: string): void {
    const tableToChange = this.canvas?.getObjects('group')
      .find(group => group?.data.resourceId === resourceId);

    if (!tableToChange) {
      return;
    }

    this.changeTableColor(tableToChange as fabric.Group, color);
  }

  init(): void {
    if (this.canvas) {
      this.canvas.dispose();
    }

    this.canvas = new fabric.Canvas(this.canvasId, {
      allowTouchScrolling: true,
      backgroundColor: this.themeColors?.backgroundColor,
      selection: false,
      preserveObjectStacking: true,
    });

    this.canvas.on('after:render', () => {
      if (!this.canvas || this.canvas.getWidth() <= 0) {
        return;
      }

      this.afterRender$.next(true);
    });

    this.drawGrid();

    this.canvas.on('object:rotating', (rotating) => {
      if (this.multiTransformSelectMode) {
        return;
      }

      const group = rotating.target as fabric.Group;
      const id = group.data?.id;
      if (!id || !group.angle) {
        return;
      }

      this.emitGroupAttributes(group);
    });

    this.canvas.on('mouse:down', (mouseDown) => {
      const target = mouseDown.target;

      if (!target && this.multiTransformSelectMode) {
        this.multiTransformSelectMode = false;
        this.multiTransformSelectModeChange.emit(false);
        this.multiTransformSelectedObjects = [];
        this.deselectAllObjects();
        this.clickedOnCanvas.emit();
        return;
      }

      if (!target) {
        this.clickedOnCanvas.emit();
        return;
      }

      if (!this.customSelectMode || !this.allowCustomSelection) {
        return;
      }

      this.customSelectTable(target, true);
    });

    this.canvas.on('object:scaling', (scaling) => {
      if (this.multiTransformSelectMode) {
        return;
      }

      const group = scaling.target as fabric.Group;
      this.scaleGroup(group);
    });

    this.canvas.on('object:moving', (moveEvent) => {
      const group = moveEvent.target as fabric.Group;
      if (!group) {
        return;
      }

      if (this.gridSnapping) {
        group.set({
          left: Math.round(group.left / this.gridCellSize) * this.gridCellSize,
          top: Math.round(group.top / this.gridCellSize) * this.gridCellSize,
        });
      }

      this.objectDragStart.emit(true);
      if (this.multiTransformSelectMode) {
        return;
      }

      this.emitGroupAttributes(group);
    });

    this.canvas.on('object:modified', (modified: fabric.IEvent<MouseEvent>) => {
      const group = modified.target as fabric.Group;
      this.checkGroupCoords(group);
      this.scaleGroup(group);
      this.canvas?.renderAll();

      const getProps = (target: fabric.Group): CanvasObjectSelected => {
        const { size, shape } = this.getObjects(target);

        return {
          size,
          shape,
          width: target?.width,
          height: target?.height,
          id: target?.data?.id,
          resourceId: target?.data?.resourceId,
          isTable: target?.data?.resourceId !== undefined,
          top: target?.top,
          left: target?.left,
          rotation: this.normalizeRotation(target?.angle ?? 0),
          sectionId: target?.data?.sectionId,
        };
      };

      if (group?.data?.id) {
        this.objectDragEnd.emit(getProps(group));
        return;
      }

      group.getObjects().forEach((item) => {
        group.removeWithUpdate(item);
        item.angle = this.normalizeRotation(item.angle ?? 0);
        this.objectDragEnd.emit(getProps(item as fabric.Group));
        group.addWithUpdate(item);
      });
    });

    this.canvas.on('selection:created', (selectionCreated) => {
      if (!selectionCreated.selected || selectionCreated?.e?.shiftKey) {
        return;
      }

      const [group] = selectionCreated.selected;
      this.multiTransform(selectionCreated);

      if (this.multiTransformSelectMode) {
        return;
      }

      if (this.customSelectMode) {
        this.canvas?.discardActiveObject();
      }

      this.emitGroupAttributes(group as fabric.Group);
    });

    this.canvas.on('selection:updated', (selectionUpdated) => {
      if (!selectionUpdated.selected || selectionUpdated?.e?.shiftKey) {
        return;
      }

      const [group] = selectionUpdated.selected;
      this.multiTransform(selectionUpdated);

      if (this.multiTransformSelectMode) {
        return;
      }

      if (this.customSelectMode) {
        this.canvas?.discardActiveObject();
      }

      this.emitGroupAttributes(group as fabric.Group);
    });

    this.canvas.on('selection:cleared', () => {
      if (this.multiTransformSelectedObjects) {
        return;
      }

      if (!this.selectedObjectId || this.customSelectMode) {
        return;
      }

      this.selectTableById(this.selectedObjectId);
    });

    if (!this.canvasAvailableAreaRef || !this.scrollbarRef) {
      return;
    }

    this.setCanvasDimensions();

    const scrollSub = this.scrollbarRef.scrolled$.subscribe(() => {
      if (!this.scrollbarRef) {
        return;
      }

      const { scrollTop, scrollLeft } = this.scrollbarRef;
      this.canvas?.absolutePan({
        x: scrollLeft,
        y: scrollTop,
      });
    });

    this.subscriptions.add(scrollSub);
  }

  checkGroupCoords(group: fabric.Group): void {
    if (!group.aCoords) {
      return;
    }

    const SCROLLBAR_WIDTH = 16;
    const cornerCoords = ['bl', 'br', 'tl', 'tr'];
    const widthCanvas = this.targetCanvasWidth - this.canvasAreaOffset - SCROLLBAR_WIDTH;
    const heightCanvas = this.targetCanvasHeight - this.canvasTopOffset - SCROLLBAR_WIDTH;
    const leftBorder = this.canvasAreaOffset;
    const topBorder = this.canvasTopOffset;
    let xOff = 0;
    let yOff = 0;

    Object.entries(group.aCoords)
      .filter(([key]) => cornerCoords.includes(key))
      .forEach(([, point]) => {
        if (point.x < leftBorder) {
          const value = leftBorder - point.x;
          if (xOff < value) {
            xOff = value;
          }
        }

        if (point.y < topBorder) {
          const value = topBorder - point.y;
          if (yOff < value) {
            yOff = value;
          }
        }

        if (point.x > widthCanvas) {
          const value = widthCanvas - point.x;
          if (xOff > value) {
            xOff = value;
          }
        }

        if (point.y > heightCanvas) {
          const value = heightCanvas - point.y;
          if (yOff > value) {
            yOff = value;
          }
        }
      });

    if (group.left !== undefined && xOff !== 0) {
      group.set('left', group.left + xOff);
    }

    if (group.top !== undefined && yOff !== 0) {
      group.set('top', group.top + yOff);
    }
  }

  multiTransform(selectedEvent: fabric.IEvent<MouseEvent>): void {
    if (!this.canvas) {
      return;
    }

    this.canvas.selection = this.multiTransformSelectMode;
    if (!this.multiTransformSelectMode || !selectedEvent.selected) {
      return;
    }

    const [selectedObject] = selectedEvent.selected;
    this.multiTransformSelectedObjects.push(selectedObject);

    const activeSelection = new fabric.ActiveSelection(
      this.multiTransformSelectedObjects,
      { canvas: this.canvas },
    );
    activeSelection.setControlsVisibility({
      mt: false,
      mb: false,
      ml: false,
      mr: false,
      tr: false,
      tl: false,
      bl: false,
      br: false,
    });

    this.activeSelection = activeSelection;
    this.canvas.setActiveObject(activeSelection, {
      ...selectedEvent.e,
      shiftKey: true,
    } as MouseEvent);
    this.canvas.requestRenderAll();
  }

  setObjectAsModifiedById(id: string): void {
    const { group } = this.getObjectsById(id);
    if (!this.canvas || !group) {
      return;
    }

    this.canvas.fire('object:modified', { target: group });
  }

  setCanvasDimensions(): void {
    if (!this.canvas || !this.canvasAvailableAreaRef) {
      return;
    }

    const { offsetWidth, offsetHeight } = this.canvasAvailableAreaRef.nativeElement as HTMLElement;
    const OFFSET_FOR_SCROLLBAR_PX = 16;
    this.actualCanvasWidth = offsetWidth - OFFSET_FOR_SCROLLBAR_PX;
    this.actualCanvasHeight = offsetHeight - OFFSET_FOR_SCROLLBAR_PX;
    this.canvas.setWidth(this.actualCanvasWidth);
    this.canvas.setHeight(this.actualCanvasHeight);
  }

  selectObjectByResourceId(resourceId: string): void {
    this.selectObjectByProperty('resourceId', resourceId);
  }

  selectTableById(id: string): void {
    this.selectObjectByProperty('id', id);
  }

  detectChanges(): void {
    if (!this.canvas || !this.canvas.getContext()) {
      return;
    }

    this.canvas.renderAll();
  }

  deselectAllObjects(): void {
    if (this.customSelectMode) {
      this.removeCustomSelectionBoxes();
    }

    this.selectedObjectId = undefined;

    if (!this.canvas) {
      return;
    }

    if (this.canvas.getActiveObjects().length > 1 && this.activeSelection) {
      this.activeSelection.getObjects().forEach((activeItem) => {
        this.activeSelection?.removeWithUpdate(activeItem);
      });
      this.activeSelection = undefined;
      this.canvas.renderAll();
      return;
    }

    this.canvas.discardActiveObject().renderAll();
  }

  removeObject(id: string): void {
    const objectToRemove = this.canvas?.getObjects('group')
      .find(group => group?.data?.id === id);

    if (objectToRemove) {
      this.canvas?.remove(objectToRemove);
    }
  }

  selectResourcesByIds(ids: string[]): void {
    this.deselectAllObjects();
    this.canvas?.renderAll();

    ids.forEach((id) => {
      this.selectObjectByResourceId(id);
    });
  }

  selectTablesByIds(ids: string[]): void {
    this.deselectAllObjects();
    this.canvas?.renderAll();

    ids.forEach((id) => {
      this.selectTableById(id);
    });
  }

  getCurrentScrollLeft(): number {
    if (!this.scrollbarRef) {
      return 0;
    }

    return this.scrollbarRef.scrollLeft;
  }

  canvasScrollLeftTo(deltaX: number): void {
    if (!this.scrollbarRef) {
      return;
    }

    const { scrollLeft } = this.scrollbarRef;
    this.scrollbarRef.scrollXTo(scrollLeft + deltaX);
  }

  mouseWheelScroll = (scroll: WheelEvent): void => {
    if (!this.scrollbarRef) {
      return;
    }

    const { scrollLeft, scrollTop } = this.scrollbarRef;
    if (scroll.deltaX !== 0) {
      this.scrollbarRef.scrollXTo(scroll.deltaX + scrollLeft);
    }

    if (scroll.deltaY !== 0 && !scroll.shiftKey) {
      this.scrollbarRef.scrollYTo(scroll.deltaY + scrollTop);
    } else if (scroll.shiftKey) {
      this.scrollbarRef.scrollXTo(scroll.deltaY + scrollLeft);
    }
  };

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.canvas?.removeListeners();
    this.canvas?.dispose();

    window.removeEventListener('resize', this.windowResizeBound);
    document.removeEventListener('mousedown', this.dragStart);
    document.removeEventListener('touchstart', this.dragStart);
    document.removeEventListener('mousemove', this.drag);
    document.removeEventListener('touchmove', this.drag);
    document.removeEventListener('mouseup', this.dragEnd);
    document.removeEventListener('touchend', this.dragEnd);
  }

  windowResize = (caller: string): void => {
    this.rerenderCanvas$.next(caller);
  };

  windowResizeBound = (): void => {
    this.windowResize('window resize event');
  };

  private autoZoomInternal(tables: FloorPlanTable[]): void {
    if (!tables.length) {
      return;
    }

    const mostTopLeftPos = {
      x: this.actualCanvasWidth,
      y: this.actualCanvasHeight,
    };
    const mostBottomRightPos = {
      x: 0,
      y: 0,
    };

    tables.forEach((table) => {
      const x1 = table.left;
      const y1 = table.top;
      const x2 = table.left + table.width;
      const y2 = table.top + table.height;

      if (x1 < mostTopLeftPos.x) { mostTopLeftPos.x = x1; }
      if (y1 < mostTopLeftPos.y) { mostTopLeftPos.y = y1; }
      if (x2 > mostBottomRightPos.x) { mostBottomRightPos.x = x2; }
      if (y2 > mostBottomRightPos.y) { mostBottomRightPos.y = y2; }
    });

    this.canvas?.setZoom(1);
    const contentWidth = (mostBottomRightPos.x - mostTopLeftPos.x);
    const contentHeight = (mostBottomRightPos.y - mostTopLeftPos.y);
    const left = mostTopLeftPos.x;
    const top = mostTopLeftPos.y;

    if (this.showAutoZoomRegion) {
      const debugBox = new fabric.Rect({
        width: contentWidth,
        height: contentHeight,
        fill: 'transparent',
        stroke: 'red',
        strokeWidth: 1,
        opacity: 0.3,
        evented: false,
        left,
        top,
      });
      this.canvas?.add(debugBox);
    }

    const viewportParent = this.canvasAvailableAreaRef?.nativeElement.getBoundingClientRect();
    const viewportWidth = viewportParent.width;
    const viewportHeight = viewportParent.height;
    const controlsHeight = (this.controlsRef?.nativeElement.offsetHeight + this.canvasTopOffset);

    let finalZoom = this.canvas?.getZoom() || 1;
    const currentStepIndex = this.zoomSteps.findIndex(step => step === finalZoom);
    let bothDimensionsFit = false;

    for (let i = currentStepIndex; i >= 0; i--) {
      if (bothDimensionsFit) {
        continue;
      }
      finalZoom = this.zoomSteps[i];
      const widthFits = viewportWidth >= (contentWidth * this.zoomSteps[i]) + this.canvasAreaOffset;
      const heightFits = viewportHeight >= (contentHeight * this.zoomSteps[i]) + (controlsHeight / finalZoom);

      if (widthFits && heightFits) {
        bothDimensionsFit = true;
      }
    }

    this.canvas?.absolutePan({
      x: Math.max((left), 0),
      y: Math.max(top - ((controlsHeight / finalZoom)), 0),
    });

    this.setZoom(finalZoom);
    this.redrawScrollbars('autoZOomInternal()');
  }

  private changeTableColor(group: fabric.Group, color: string): void {
    if (!group) {
      return;
    }

    const { shapeObject } = this.getObjects(group);
    if (!shapeObject) {
      return;
    }

    shapeObject.set('fill', color);
  }

  private getCurrentZoomStep(newZoom: number): number {
    const foundIndex = this.zoomSteps.findIndex(step => step === newZoom);
    if (foundIndex !== -1) {
      return foundIndex;
    }

    let minZoomDifferenceIndex = Infinity;
    let minZoomDifference = Infinity;

    this.zoomSteps.forEach((zoomLevel, index) => {
      const difference = Math.abs(newZoom - zoomLevel);

      if (difference < minZoomDifference) {
        minZoomDifference = difference;
        minZoomDifferenceIndex = index;
      }
    });

    if (minZoomDifferenceIndex !== Infinity) {
      return minZoomDifferenceIndex;
    }

    return this.defaultZoomStepIndex;
  }

  private setZoom(targetZoom: number): void {
    if (!this.canvas) {
      return;
    }

    if (this.actualCanvasWidth <= 0) {
      return;
    }

    let finalZoom = targetZoom;
    const widthUpperLimit = this.actualCanvasWidth / this.targetCanvasWidth;
    const heightUpperLimit = this.actualCanvasHeight / this.targetCanvasHeight;

    if (targetZoom > this.maxZoom) {
      finalZoom = this.maxZoom;
    }

    if (targetZoom < this.minZoom) {
      finalZoom = this.minZoom;
    }

    if (targetZoom < widthUpperLimit) {
      finalZoom = widthUpperLimit;
    }

    if (targetZoom < heightUpperLimit) {
      finalZoom = heightUpperLimit;
    }

    if (targetZoom < widthUpperLimit && targetZoom < heightUpperLimit) {
      finalZoom = widthUpperLimit > heightUpperLimit ? widthUpperLimit : heightUpperLimit;
    }

    this.currentZoomStepIndex = this.getCurrentZoomStep(finalZoom);
    this.canvas.setZoom(finalZoom);

    this.setScrollSize(finalZoom);
  }

  private setScrollSize(zoom: number): void {
    if (!this.canvas) {
      return;
    }

    const { x, y } = this.canvas.calcViewportBoundaries().tl;
    this.scrollableAreaWidth = this.targetCanvasWidth * zoom;
    this.scrollableAreaHeight = this.targetCanvasHeight * zoom;
    this.cdr.detectChanges();

    setTimeout(() => {
      if (this.scrollbarRef instanceof FrontendScrollbarComponent) {
        this.scrollbarRef.update(x * zoom, y * zoom);
      }
    });
  }

  private stopDrag(): void {
    const clone = document.getElementById(CLONED_TABLE_ID);
    if (clone) {
      clone.remove();
    }

    if (this.dragState.dragCursor) {
      this.relocateElement(
        this.dragState.dragCursor,
        false,
        this.dragState.moveX,
        this.dragState.moveY,
        this.dragState.dragTargetWidth,
      );
      this.dragState.dragCursor = undefined;
    }
  }

  private readonly dragEnd = (): void => {
    const partyCardWithNoDrag = this.dragState.target === FloorPlanDraggable.party && !this.dragState.dragStartedWithOffset;
    if (this.dragState.target === FloorPlanDraggable.canvas || !this.scrollbarRef || partyCardWithNoDrag) {
      this.stopDrag();
      return;
    }

    this.dragState.dragStarted = false;
    const canvasBox = this.scrollbarRef.boundingBox;
    const { scrollLeft, scrollTop } = this.scrollbarRef;
    const { offsetX, offsetY } = this.getOffsetForTarget();
    const dropX = this.dragState.moveX - canvasBox.x - offsetX;
    const dropY = this.dragState.moveY - canvasBox.y - offsetY;
    const dropTarget = document.elementFromPoint(
      this.dragState.moveX - offsetX,
      this.dragState.moveY - offsetY,
    );

    if (this.dragState.target === FloorPlanDraggable.tableOrObject) {
      if (dropTarget?.tagName !== 'CANVAS') {
        this.stopDrag();
        return;
      }

      this.dropNewObject.emit([dropY + scrollTop, dropX + scrollLeft]);
    } else if (this.dragState.target === FloorPlanDraggable.party) {
      const targetTable = this.findTarget();
      if (!this.canDropOnTable(targetTable) && this.dragState.dragCursor) {
        this.stopDrag();
        return;
      }

      this.stopDrag();
      this.updatePartyWithResource.emit([String(this.dragState.partyId), String(targetTable?.data?.resourceId)]);
    }
  };

  private canDropOnTable(table?: fabric.Object): boolean {
    if (!table) {
      return false;
    }

    return table?.data?.objectType === 'table' &&
      (table?.data?.hasParty === undefined || table?.data?.hasParty === false);
  }

  private relocateElement(element: HTMLElement, shouldFloat: boolean, x = 0, y = 0, forceWidth?: number): void {
    const draggingNewTable = this.dragState.target === FloorPlanDraggable.tableOrObject;

    if (draggingNewTable && shouldFloat && !document.getElementById(CLONED_TABLE_ID)) {
      const newElement = element.cloneNode(true);
      (newElement as HTMLElement).id = CLONED_TABLE_ID;
      element.after(newElement);
    }

    element.style.position = shouldFloat ? 'fixed' : 'relative';
    element.style.top = shouldFloat ? `${y}px` : '0';
    element.style.left = shouldFloat ? `${x}px` : '0';
    element.style.zIndex = shouldFloat ? '999' : '20';
    element.style.opacity = shouldFloat && !draggingNewTable ? '0.5' : '1';
    element.style.pointerEvents = shouldFloat ? 'none' : 'all';
    element.style.minWidth = shouldFloat && forceWidth ? `${forceWidth}px` : 'auto';
    element.style.maxWidth = shouldFloat && forceWidth ? `${forceWidth}px` : 'auto';

    if (!shouldFloat) {
      element.style.transform = '';
    }

    this.dragState.dragCursor = element;
  }

  private readonly dragStart = (start: MouseEvent | TouchEvent): void => {
    const target = (start.target as HTMLElement);
    const rect = target.getBoundingClientRect();
    let currentScrollY = 0;
    let currentScrollX = 0;

    this.dragState.target = undefined;
    this.dragState.dragCursor = undefined;
    this.dragState.dragTargetWidth = undefined;
    this.dragState.dragStartedWithOffset = false;
    this.dragState.partyId = undefined;

    if (target.classList.contains('drag-table')) {
      this.dragState.target = FloorPlanDraggable.tableOrObject;
      this.dragState.dragCursor = target;
      this.dragState.dragTargetWidth = rect.width;
    } else if (target.tagName === 'CANVAS') {
      this.dragState.target = FloorPlanDraggable.canvas;
    } else if (target.classList.contains('drag-party')) {
      this.dragState.target = FloorPlanDraggable.party;
      this.dragState.partyId = target.id;
      this.dragState.dragCursor = target;
      this.dragState.dragTargetWidth = rect.width;
    }

    this.dragState.dragStarted = this.dragState.target !== undefined;
    if (this.scrollbarRef) {
      currentScrollY = this.scrollbarRef.scrollTop;
      currentScrollX = this.scrollbarRef.scrollLeft;
    }

    if (start instanceof MouseEvent) {
      const x = start.clientX;
      const y = start.clientY;
      this.dragState.startX = x + currentScrollX;
      this.dragState.startY = y + currentScrollY;
      this.dragState.offsetX = x - rect.x;
      this.dragState.offsetY = y - rect.y;
    } else if (start instanceof TouchEvent && start.touches?.length > 0) {
      const x = start.touches[0].clientX;
      const y = start.touches[0].clientY;
      this.dragState.startX = x + currentScrollX;
      this.dragState.startY = y + currentScrollY;
      this.dragState.offsetX = x - rect.x;
      this.dragState.offsetY = y - rect.y;
    }
  };

  private readonly drag = (drag: MouseEvent | TouchEvent): void => {
    if (!this.dragState.dragStarted) {
      return;
    }

    if (drag instanceof MouseEvent) {
      this.dragState.moveX = drag.clientX;
      this.dragState.moveY = drag.clientY;

      if (!this.dragState.dragStartedWithOffset) {
        this.startDrag();
      }
    } else if (window?.TouchEvent &&
      drag instanceof TouchEvent &&
      drag.touches?.length > 0 &&
      !this.multiTransformSelectMode
    ) {
      this.dragState.moveX = drag.touches[0].clientX;
      this.dragState.moveY = drag.touches[0].clientY;

      if (
        this.dragState.target === FloorPlanDraggable.canvas &&
        this.selectedObjectId === undefined && this.scrollbarRef
      ) {
        this.scrollbarRef.scrollYTo(this.dragState.startY - this.dragState.moveY);
        this.scrollbarRef.scrollXTo(this.dragState.startX - this.dragState.moveX);
      }
    }

    if (this.dragState.dragCursor && !this.dragState.dragStartedWithOffset) {
      const scrollX = this.scrollbarRef?.scrollLeft || 0;
      const scrollY = this.scrollbarRef?.scrollTop || 0;
      const moveX = Math.abs(this.dragState.startX - this.dragState.moveX - scrollX);
      const moveY = Math.abs(this.dragState.startY - this.dragState.moveY - scrollY);

      const dragStartThresholdPx = 100;
      if (moveX > dragStartThresholdPx) {
        this.startDrag();
      }
    }

    if (this.dragState.dragCursor && this.dragState.dragStartedWithOffset) {
      const { offsetX, offsetY } = this.getOffsetForTarget();
      this.dragState.dragCursor.style.top = `${this.dragState.moveY - offsetY}px`;
      this.dragState.dragCursor.style.left = `${this.dragState.moveX - offsetX - this.leftLayoutOffsetPx}px`;

      if (this.dragState.target === FloorPlanDraggable.party) {
        const targetTable = this.findTarget();
        this.dragState.dragCursor.style.opacity = this.canDropOnTable(targetTable) ? '1' : '0.5';
      }
    }
  };

  private readonly startDrag = (): void => {
    if (!this.dragState.dragCursor) {
      return;
    }

    this.dragState.dragStartedWithOffset = true;

    const { offsetX, offsetY } = this.getOffsetForTarget();
    this.relocateElement(
      this.dragState.dragCursor,
      true,
      this.dragState.moveX - offsetX,
      this.dragState.moveY - offsetY,
      this.dragState.dragTargetWidth,
    );

    if (this.dragState.target === FloorPlanDraggable.tableOrObject) {
      this.dragState.dragCursor.style.transformOrigin = '0 0';
      this.dragState.dragCursor.style.transform = `scale(${this.canvas?.getZoom()})`;
    }

    this.htmlDragStart.emit(true);
  };

  private getOffsetForTarget(): { offsetX: number; offsetY: number } {
    let offsetX = this.dragState.offsetX;
    let offsetY = this.dragState.offsetY;

    if (this.dragState.target === FloorPlanDraggable.tableOrObject && this.canvas) {
      offsetX *= this.canvas.getZoom();
      offsetY *= this.canvas.getZoom();
    }

    return {
      offsetX,
      offsetY,
    };
  }

  private findTarget(): fabric.Object | undefined {
    if (!this.canvas) {
      return undefined;
    }

    return this.canvas.findTarget(new MouseEvent('mousedown', {
      bubbles: false,
      cancelable: true,
      view: window,
      clientX: this.dragState.moveX,
      clientY: this.dragState.moveY,
    }), false);
  }

  private scaleGroup(group: fabric.Group, makeActive = true): void {
    if (!group.height || !group.width || !group.scaleY || !group.scaleX || this.multiTransformSelectMode) {
      return;
    }

    const groupBaseWidth = group.width;
    const groupBaseHeight = group.height;
    const groupWidth = groupBaseWidth * group.scaleX;
    const groupHeight = groupBaseHeight * group.scaleY;
    const isTable = group.data?.objectType === 'table';
    const isInSection = group.data?.sectionId !== '' && group.data?.sectionId !== undefined;

    if (groupWidth < CANVAS_MIN_SHAPE_SIZE) {
      group.set('scaleX', 1);
      group.set('width', CANVAS_MIN_SHAPE_SIZE);
    }

    if (groupHeight < CANVAS_MIN_SHAPE_SIZE) {
      group.set('scaleY', 1);
      group.set('height', CANVAS_MIN_SHAPE_SIZE);
    }

    group.getObjects().forEach((figure) => {
      if (!figure || !group.height || !group.width || !group.scaleY || !group.scaleX) {
        return;
      }

      const minWidth = CANVAS_MIN_SHAPE_SIZE;
      const minHeight = CANVAS_MIN_SHAPE_SIZE;

      const width = Math.max(group.width * group.scaleX, minWidth);
      const height = Math.max(group.height * group.scaleY, minHeight);

      figure.set('scaleX', Math.max(group.width, minWidth) / (width));
      figure.set('scaleY', Math.max(group.height, minHeight) / (height));

      if (figure.hasOwnProperty('radius')) {
        (figure as fabric.Circle).set('radius', width / 2);
      }

      if (figure.data?.type !== 'name' && figure.data?.type !== 'size' && figure.data?.type !== 'time') {
        let offset = this.borderWidth;

        if (figure.data?.type === 'shape' && isTable && isInSection) {
          offset = this.sectionBorderWidth * 2;
        } else if (isTable && isInSection) {
          offset = this.sectionBorderWidth;
        }

        figure.set('width', width - offset);
        figure.set('height', height - offset);
      }

      if (figure.data?.type === 'name') {
        const reduceBy = isTable ? 0 : this.sectionBorderWidth * 2;
        (figure as fabric.Text).set('text', this.getTextForShapeWidth(figure.data.fullText, width - reduceBy));
      }

      if (makeActive) {
        this.emitGroupAttributes(group);
      }
    });
  }

  private setTransformControlsState(
    objects: fabric.Object[],
    controls: string[],
    enabled: boolean,
    disableMove = false,
  ): void {
    objects.forEach((obj) => {
      obj.setControlsVisibility(this.getTransformControlsList(controls, enabled));
      obj.lockMovementY = disableMove;
      obj.lockMovementX = disableMove;
      obj.selectable = !disableMove;
    });
  }

  private createSelectionBox(original: fabric.Object, selectionId: string): void {
    const strokeWidth = 8;
    const width = original.width * original.scaleX + strokeWidth;
    const height = original.height * original.scaleY + strokeWidth;
    const angle = original.angle;

    const { x, y } = original.getCenterPoint();

    const group = new fabric.Group([], {
      top: y,
      left: x,
      scaleX: 1,
      scaleY: 1,
      originX: 'center',
      originY: 'center',
      data: {
        selectionId,
        selectionBox: true,
      },
      selectable: false,
      width,
      height,
      angle,
      objectCaching: false,
    });

    const rect = new fabric.Rect({
      width,
      height,
      originX: 'center',
      originY: 'center',
      fill: 'rgba(0, 0, 0, 0)',
      stroke: '#00B8F2',
      strokeWidth: 1,
      objectCaching: false,
    });

    const labelWidth = 30;
    const labelHeight= (height / 2) + 10;

    const label = new fabric.Rect({
      top: labelHeight,
      width: labelWidth,
      originX: 'center',
      originY: 'center',
      height: 10,
      fill: '#4DDFFF',
      strokeWidth: 0,
      objectCaching: false,
    });

    const text = new fabric.Text('Selected', {
      top: labelHeight,
      data: { selectionId },
      width: labelWidth,
      originX: 'center',
      originY: 'center',
      fontFamily: 'Source Sans Pro',
      fontSize: 8,
      fontWeight: 400,
      objectCaching: false,
    });

    group.add(rect);
    group.add(label);
    group.add(text);

    this.canvas?.add(group);
  }

  private customSelectTable(target: fabric.Object, isActionSourceUser: boolean): void {
    if (!this.allowObjectCustomSelection && target.data?.objectType === 'object') {
      return;
    }

    if (
      this.allowOnlySectionIdSelection !== undefined &&
      target.data?.sectionId !== '' &&
      target.data?.sectionId !== this.allowOnlySectionIdSelection
    ) {
      return;
    }

    const id = target.data?.id;
    const group: fabric.Group = this.getObjectsById(id)?.group;

    if (!id) {
      const selectionId = target.data?.selectionId;
      if (selectionId) {
        this.multiSelectedObjectIds.delete(selectionId);
        this.removeCustomSelectionBoxes(selectionId);
      }

      return;
    }

    if (!this.customMultiSelectEnabled || (isActionSourceUser && !this.allowMultiSelectByUser)) {
      this.multiSelectedObjectIds.forEach((selectionId) => {
        this.removeCustomSelectionBoxes(selectionId, isActionSourceUser);
      });
      this.multiSelectedObjectIds.clear();
    }

    this.objectMultiSelected.emit({
      id,
      isActionSourceUser,
      resourceId: group.data?.resourceId,
      isTable: group.data?.objectType === 'table',
      selected: true,
    });

    this.createSelectionBox(target, id);
    this.multiSelectedObjectIds.add(id);
  }

  private removeCustomSelectionBoxes(selectionId?: string, isActionSourceUser = false): void {
    const allBoxes = this.canvas?.getObjects('group').filter(group => group.data?.selectionBox);
    if (!allBoxes) {
      return;
    }

    allBoxes.forEach((box) => {
      if (box.data?.selectionId === selectionId || (!selectionId && box.data?.selectionBox)) {
        this.canvas?.remove(box);
        if (!selectionId) {
          return;
        }

        this.objectMultiSelected.emit({
          id: selectionId,
          selected: false,
          isTable: false,
          isActionSourceUser,
        });
      }
    });
  }

  private emitGroupAttributes(group: fabric.Group): void {
    const width = group.width * group.scaleX;
    const height = group.height * group.scaleY;

    const { shape, size, nameText } = this.getObjects(group);
    const name = nameText?.data?.fullText;

    const result: CanvasObjectSelected = {
      size,
      name,
      shape,
      width,
      height,
      id: group.data?.id,
      left: group.left,
      top: group.top,
      sectionId: group.data?.sectionId,
      resourceId: group.data?.resourceId,
      isTable: group.data?.objectType === 'table',
      rotation: this.normalizeRotation(group.angle ?? 0),
    };

    group.set('width', width);
    group.set('scaleX', 1);
    group.set('height', height);
    group.set('scaleY', 1);

    this.objectSelected.emit(result);
  }

  private getTransformControlsList(controlNames: string[], enabled: boolean): Record<string, boolean> {
    const controlToDisable: Record<string, boolean> = {};
    controlNames.forEach((controlName) => {
      controlToDisable[controlName] = enabled;
    });

    return controlToDisable;
  }

  private getObjectsById(id: string): CanvasTableChildren {
    const group = this.canvas?.getObjects('group')
      .find(item => item.data?.id === id) as fabric.Group;

    if (!group) {
      return {
        group,
        size: TableSizes.custom,
        shape: Shapes.rectangular,
      };
    }

    return this.getObjects(group);
  }

  private getObjects(group: fabric.Group): CanvasTableChildren {
    if (!group) {
      return {
        group,
        size: TableSizes.custom,
        shape: Shapes.rectangular,
      };
    }

    const objects = group.getObjects();
    let nameText: fabric.Text | undefined;
    let capacityText: fabric.Text | undefined;
    let timeText: fabric.Text | undefined;
    let shapeObject: fabric.Circle | fabric.Rect | undefined;

    objects.forEach((el) => {
      if (el.data?.type === 'name') {
        nameText = el as fabric.Text;
      }

      if (el.data?.type === 'size') {
        capacityText = el as fabric.Text;
      }

      if (el.data?.type === 'time') {
        timeText = el as fabric.Text;
      }

      if (el.data?.type === 'shape') {
        shapeObject = el as fabric.Circle | fabric.Rect;
      }
    });

    let size = TableSizes.custom;
    let shape = Shapes.rectangular;

    const width = group.width * group.scaleX;
    const height = group.height * group.scaleY;

    if (shapeObject?.hasOwnProperty('radius')) {
      shape = Shapes.circle;
    } else if (width === height) {
      shape = Shapes.square;
    }

    const dimensions = SHAPES_DIMENSIONS[shape];
    const sizesToIterate = [TableSizes.small, TableSizes.medium, TableSizes.large];
    sizesToIterate.forEach((sizeToCheck) => {
      if (sizeToCheck === TableSizes.custom) {
        return;
      }

      if (dimensions[sizeToCheck].width === width && dimensions[sizeToCheck].height === height) {
        size = sizeToCheck;
      }
    });

    return {
      group,
      shape,
      size,
      shapeObject,
      timeText,
      nameText,
      capacityText,
    };
  }

  private initObjectPattern(): void {
    const patternSize = 50;
    const linesCount = 10;
    const half = 2;
    const strokeWidth = 0.5;

    const patternSourceCanvas = new fabric.StaticCanvas(null, {
      width: patternSize,
      height: patternSize,
      backgroundColor: this.themeColors?.shapeFill,
    });

    const lineStep = patternSize / linesCount;
    for (let i = -half; i < (linesCount * half) + 1; i++) {
      const line = new fabric.Line([
        (lineStep * i) + linesCount,
        0,
        (lineStep * i) - linesCount,
        patternSize,
      ],
      { objectCaching: false });

      line.strokeWidth = strokeWidth;
      line.stroke = this.themeColors?.shapeStroke;
      patternSourceCanvas.add(line);
    }

    patternSourceCanvas.renderAll();
    this.objectPattern = new fabric.Pattern({
      source: patternSourceCanvas.getElement().toDataURL('png'),
      repeat: 'repeat',
    });
  }

  private selectObjectByProperty(propertyName: string, propertyValue: string): void {
    if (!this.canvas) {
      return;
    }

    const objectToSelect = this.canvas?.getObjects('group')
      .find(group => group?.data[propertyName] === propertyValue);

    if (!objectToSelect || objectToSelect.data?.disabled) {
      return;
    }

    if (this.customSelectMode) {
      this.customSelectTable(objectToSelect, false);
      this.emitGroupAttributes(objectToSelect as fabric.Group);

      return;
    }

    this.canvas.setActiveObject(objectToSelect);
    this.emitGroupAttributes(objectToSelect as fabric.Group);
  }

  private drawGrid(): void {
    if (!this.showGrid || !this.canvas) {
      return;
    }

    const linesCount = this.targetCanvasWidth / this.gridCellSize;
    for (let i = 0; i < linesCount; i++) {
      const strokeWidth = i % 5 === 0 ? 2 : 1;
      const stroke = this.themeColors?.dividerColor;

      this.canvas.add(new fabric.Line(
        [i * this.gridCellSize, 0, i * this.gridCellSize, this.targetCanvasHeight],
        {
          type: 'line',
          strokeWidth,
          stroke,
          hasBorders: false,
          lockMovementX: true,
          lockMovementY: true,
          hoverCursor: 'default',
          moveCursor: 'default',
          hasControls: false,
          selectable: false,
          evented: false,
          objectCaching: false,
          opacity: this.isOnFreePlan ? 0.5 : 1,
        },
      ));
      this.canvas.add(new fabric.Line(
        [0, i * this.gridCellSize, this.targetCanvasWidth, i * this.gridCellSize],
        {
          type: 'line',
          strokeWidth,
          stroke,
          hasBorders: false,
          lockMovementX: true,
          lockMovementY: true,
          hoverCursor: 'default',
          moveCursor: 'default',
          hasControls: false,
          selectable: false,
          evented: false,
          objectCaching: false,
          opacity: this.isOnFreePlan ? 0.5 : 1,
        },
      ));
    }
  }

  private normalizeRotation(rotation: number): number {
    const MAX_ANGLE = 360;
    return rotation + (Math.ceil(-rotation / MAX_ANGLE) * MAX_ANGLE);
  }

  private themeToColors = (theme: AppTheme): CanvasColors => (theme === AppTheme.light ? CANVAS_LIGHT_THEME : CANVAS_DARK_THEME);

  private getTextForShapeWidth(text: string, targetWidth: number): string {
    const textWidth = this.canvas?.getContext()?.measureText(text).width;
    if (!textWidth) {
      return text;
    }

    if (textWidth > targetWidth) {
      const HALF_FOR_EACH_SIDE = 2;
      const SPACE_FOR_PERIODS = 3;
      const charWidth = textWidth / text.length;
      const maxCharacters = Math.floor(((targetWidth / charWidth) - SPACE_FOR_PERIODS) / HALF_FOR_EACH_SIDE);
      const resultText = `${text.substring(0, maxCharacters)}...${text.substring(text.length - maxCharacters, text.length)}`;

      return this.getTextForShapeWidth(resultText, targetWidth);
    }

    return text;
  }
}
